// src/hooks/useDiscountPOS.js
import { useState, useCallback } from "react";
import discountPOSService from "@/services/discountPOSService";

// ============================================
// DUMMY DATA FLAG - Set to false when API is ready
// ============================================
const USE_DUMMY = false;

// Dummy discount data for testing UI
const DUMMY_DISCOUNTS = [
  {
    id: 1,
    code: "WEEKEND20",
    name: "Weekend Special 20% Off",
    description: "Get 20% off on weekends",
    discountType: "percentage",
    discountValue: 20,
    appliesTo: "bill",
    idMenuItems: [],
    idCategories: [],
    minBillAmount: 500,
    maxDiscountAmount: 200,
    buyQuantity: null,
    getQuantity: null,
    eligible: true,
    eligibilityMessage: "Applicable",
  },
  {
    id: 2,
    code: "FLAT100",
    name: "Flat ₹100 Off",
    description: "Flat ₹100 off on orders above ₹800",
    discountType: "fixed",
    discountValue: 100,
    appliesTo: "bill",
    idMenuItems: [],
    idCategories: [],
    minBillAmount: 800,
    maxDiscountAmount: null,
    buyQuantity: null,
    getQuantity: null,
    eligible: false,
    eligibilityMessage: "Add ₹300 more to qualify",
  },
  {
    id: 3,
    code: "BOGO",
    name: "Buy 1 Get 1 Free",
    description: "Buy one pizza, get one free",
    discountType: "bogo",
    discountValue: 0,
    appliesTo: "item",
    idMenuItems: [101, 102, 103],
    idCategories: [],
    minBillAmount: 0,
    maxDiscountAmount: null,
    buyQuantity: 1,
    getQuantity: 1,
    eligible: false,
    eligibilityMessage: "Add pizza items to qualify",
  },
  {
    id: 4,
    code: "DINEIN10",
    name: "Dine-In 10% Off",
    description: "10% off for dine-in orders",
    discountType: "percentage",
    discountValue: 10,
    appliesTo: "bill",
    idMenuItems: [],
    idCategories: [],
    minBillAmount: 200,
    maxDiscountAmount: 150,
    buyQuantity: null,
    getQuantity: null,
    eligible: true,
    eligibilityMessage: "Applicable",
  },
  {
    id: 5,
    code: "DESSERT50",
    name: "50% Off on Desserts",
    description: "Half price on all desserts",
    discountType: "percentage",
    discountValue: 50,
    appliesTo: "category",
    idMenuItems: [],
    idCategories: [5, 6],
    minBillAmount: 0,
    maxDiscountAmount: null,
    buyQuantity: null,
    getQuantity: null,
    eligible: true,
    eligibilityMessage: "Applicable",
  },
];

/**
 * Maps discount from API format to UI format
 */
const mapDiscountFromApi = (d = {}) => ({
  id: d.id,
  code: d.code,
  name: d.name,
  description: d.description,
  discountType: d.discountType, // percentage, fixed, bogo, complimentary, bundle, tiered
  discountValue: d.discountValue,
  appliesTo: d.appliesTo, // bill, item, category
  idMenuItems: d.idMenuItems || [],
  idCategories: d.idCategories || [],
  minBillAmount: d.minBillAmount || 0,
  maxDiscountAmount: d.maxDiscountAmount || null,
  buyQuantity: d.buyQuantity || null,
  getQuantity: d.getQuantity || null,
  freeItemIds: d.freeItemIds || [],
  bundlePrice: d.bundlePrice || 0,
  tierConfig: d.tierConfig || [],
  validFrom: d.validFrom,
  validUntil: d.validUntil,
  startTime: d.startTime,
  endTime: d.endTime,
  daysOfWeek: d.daysOfWeek || [],
  orderTypes: d.orderTypes || [],
  usageLimit: d.usageLimit,
  usageCount: d.usageCount || 0,
  usagePerCustomer: d.usagePerCustomer,
  isActive: d.isActive,
  // UI-specific fields from suggestions API
  eligible: d.eligible ?? true,
  eligibilityMessage: d.eligibilityMessage || "Applicable",
});

/**
 * Calculate discount amount based on discount type and cart
 * @param {Object} discount - The discount object
 * @param {Array} cartItems - Cart items with { menuItemId, categoryId, price, quantity }
 * @param {number} subtotal - Cart subtotal
 */
export const calculateDiscountAmount = (discount, cartItems = [], subtotal = 0) => {
  if (!discount) return 0;

  const { discountType, discountValue, appliesTo, idMenuItems, idCategories, maxDiscountAmount } = discount;

  let applicableAmount = subtotal;

  // For item/category specific discounts, calculate applicable amount
  if (appliesTo === "item" && idMenuItems?.length > 0) {
    applicableAmount = cartItems
      .filter((item) => idMenuItems.includes(item.menuItemId))
      .reduce((sum, item) => sum + (item.price * item.quantity), 0);
  } else if (appliesTo === "category" && idCategories?.length > 0) {
    applicableAmount = cartItems
      .filter((item) => idCategories.includes(item.categoryId))
      .reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }

  let discountAmount = 0;

  switch (discountType) {
    case "percentage":
      discountAmount = (applicableAmount * discountValue) / 100;
      // Apply max discount cap if set
      if (maxDiscountAmount && discountAmount > maxDiscountAmount) {
        discountAmount = maxDiscountAmount;
      }
      break;

    case "fixed":
      discountAmount = Math.min(discountValue, applicableAmount);
      break;

    case "bogo": {
      // BOGO calculation - find applicable items and calculate free item value
      const { buyQuantity = 1, getQuantity = 1 } = discount;
      let applicableItems;
      if (appliesTo === "item" && idMenuItems?.length > 0) {
        applicableItems = cartItems.filter((item) => idMenuItems.includes(item.menuItemId));
      } else if (appliesTo === "category" && idCategories?.length > 0) {
        applicableItems = cartItems.filter((item) => idCategories.includes(item.categoryId));
      } else {
        applicableItems = cartItems;
      }

      applicableItems.forEach((item) => {
        const sets = Math.floor(item.quantity / (buyQuantity + getQuantity));
        const freeItems = sets * getQuantity;
        discountAmount += freeItems * item.price;
      });
      break;
    }

    case "complimentary": {
      // Cross-item complimentary: buy trigger items, get different items free
      const { buyQuantity: compBuy = 1, getQuantity: compGet = 1, freeItemIds = [] } = discount;
      // Count trigger items in cart (trigger items are idMenuItems or idCategories)
      let triggerCount = 0;
      if (appliesTo === "item" && idMenuItems?.length > 0) {
        triggerCount = cartItems
          .filter((item) => idMenuItems.includes(item.menuItemId))
          .reduce((sum, item) => sum + item.quantity, 0);
      } else if (appliesTo === "category" && idCategories?.length > 0) {
        triggerCount = cartItems
          .filter((item) => idCategories.includes(item.categoryId))
          .reduce((sum, item) => sum + item.quantity, 0);
      } else {
        triggerCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
      }
      const completeSets = Math.floor(triggerCount / compBuy);
      const maxFreeItems = completeSets * compGet;

      // Find free items in cart, discount cheapest-first
      const freeCartItems = cartItems
        .filter((item) => freeItemIds.includes(item.menuItemId))
        .flatMap((item) => Array(item.quantity).fill(item.price))
        .sort((a, b) => a - b);

      const freeCount = Math.min(maxFreeItems, freeCartItems.length);
      for (let i = 0; i < freeCount; i++) {
        discountAmount += freeCartItems[i];
      }
      break;
    }

    case "bundle": {
      // Bundle deal: all idMenuItems present at combo price
      const { bundlePrice = 0 } = discount;
      if (!idMenuItems?.length || bundlePrice <= 0) break;

      // Find the minimum qty of each bundle item in the cart
      const bundleItemQtys = idMenuItems.map((menuItemId) => {
        const cartItem = cartItems.find((item) => item.menuItemId === menuItemId);
        return cartItem ? cartItem.quantity : 0;
      });
      const bundleCount = Math.min(...bundleItemQtys);
      if (bundleCount <= 0) break;

      // Sum of individual prices for one set
      const individualSum = idMenuItems.reduce((sum, menuItemId) => {
        const cartItem = cartItems.find((item) => item.menuItemId === menuItemId);
        return sum + (cartItem ? cartItem.price : 0);
      }, 0);

      discountAmount = bundleCount * (individualSum - bundlePrice);
      if (discountAmount < 0) discountAmount = 0;
      break;
    }

    case "tiered": {
      // Tiered discount: progressive slabs based on subtotal
      const { tierConfig = [] } = discount;
      if (!tierConfig.length) break;

      // Sort tiers descending by minAmount, find highest qualifying
      const sortedTiers = [...tierConfig].sort(
        (a, b) => (b.minAmount || 0) - (a.minAmount || 0)
      );
      const matchingTier = sortedTiers.find(
        (tier) => subtotal >= (tier.minAmount || 0)
      );

      if (matchingTier) {
        discountAmount = (subtotal * (matchingTier.value || 0)) / 100;
        if (maxDiscountAmount && discountAmount > maxDiscountAmount) {
          discountAmount = maxDiscountAmount;
        }
      }
      break;
    }

    default:
      discountAmount = 0;
  }

  return Math.round(discountAmount * 100) / 100; // Round to 2 decimal places
};

/**
 * Hook for managing discounts at POS level
 */
export const useDiscountPOS = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch suggested discounts based on order context
   */
  const fetchSuggestions = useCallback(async (params) => {
    try {
      setLoading(true);
      setError(null);

      // Use dummy data for testing
      if (USE_DUMMY) {
        await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate API delay
        // Simulate eligibility based on subtotal
        const subtotal = params.subtotal || 0;
        const dummyWithEligibility = DUMMY_DISCOUNTS.map((d) => ({
          ...d,
          eligible: d.minBillAmount <= subtotal && (d.appliesTo === "bill" || d.eligible),
          eligibilityMessage:
            d.minBillAmount > subtotal
              ? `Add ₹${d.minBillAmount - subtotal} more to qualify`
              : d.eligibilityMessage,
        }));
        setSuggestions(dummyWithEligibility);
        return;
      }

      const res = await discountPOSService.getSuggestions(params);
      const data = res?.data ?? res ?? [];
      const discounts = Array.isArray(data) ? data : (data?.data ?? []);
      setSuggestions(discounts.map(mapDiscountFromApi));
    } catch (err) {
      console.error("Failed to fetch discount suggestions:", err);
      setError(err?.response?.data?.message || "Failed to load discount suggestions");
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Validate a discount code
   */
  const validateCode = useCallback(async (code, context) => {
    try {
      setValidating(true);
      setError(null);

      // Use dummy data for testing
      if (USE_DUMMY) {
        await new Promise((resolve) => setTimeout(resolve, 300)); // Simulate API delay
        const foundDiscount = DUMMY_DISCOUNTS.find(
          (d) => d.code.toUpperCase() === code.toUpperCase()
        );

        if (foundDiscount) {
          const subtotal = context.subtotal || 0;
          const isEligible = foundDiscount.minBillAmount <= subtotal;

          if (isEligible) {
            return {
              valid: true,
              discount: { ...foundDiscount, eligible: true },
              message: "Discount applied successfully!",
            };
          } else {
            return {
              valid: false,
              discount: null,
              message: `Minimum bill amount is ₹${foundDiscount.minBillAmount}`,
            };
          }
        } else {
          return { valid: false, discount: null, message: "Invalid discount code" };
        }
      }

      const payload = {
        code,
        orderType: context.orderType,
        subtotal: context.subtotal,
        itemIds: context.itemIds || [],
        categoryIds: context.categoryIds || [],
        customerId: context.customerId || null,
      };
      const res = await discountPOSService.validateCode(payload);
      const data = res?.data ?? res ?? {};

      if (data.valid && data.data) {
        const discount = mapDiscountFromApi(data.data);
        return { valid: true, discount, message: data.message };
      } else {
        return { valid: false, discount: null, message: data.message || "Invalid discount code" };
      }
    } catch (err) {
      console.error("Failed to validate discount code:", err);
      const message = err?.response?.data?.message || "Failed to validate discount code";
      setError(message);
      return { valid: false, discount: null, message };
    } finally {
      setValidating(false);
    }
  }, []);

  /**
   * Apply a discount
   */
  const applyDiscount = useCallback((discount) => {
    setAppliedDiscount(discount);
    setError(null);
  }, []);

  /**
   * Remove applied discount
   */
  const removeDiscount = useCallback(() => {
    setAppliedDiscount(null);
  }, []);

  /**
   * Get customer usage for a discount
   */
  const getCustomerUsage = useCallback(async (discountId, customerId) => {
    try {
      const res = await discountPOSService.getCustomerUsage(discountId, customerId);
      return res?.data ?? res ?? null;
    } catch (err) {
      console.error("Failed to get customer usage:", err);
      return null;
    }
  }, []);

  /**
   * Clear all state
   */
  const reset = useCallback(() => {
    setSuggestions([]);
    setAppliedDiscount(null);
    setError(null);
  }, []);

  return {
    // State
    suggestions,
    appliedDiscount,
    loading,
    validating,
    error,

    // Actions
    fetchSuggestions,
    validateCode,
    applyDiscount,
    removeDiscount,
    getCustomerUsage,
    reset,

    // Utilities
    calculateDiscountAmount,
  };
};

export default useDiscountPOS;
